// Single source of truth for the Completion Receipt. Both the on-screen
// certificate and the PDF/PNG export read this flat model — derived once from the
// already-loaded IIRevResultState (no extra network call), exactly the data the
// "View Result" panel uses, plus agent/party identity from the JWT + profile.
import type { ResultType } from '../../../constants/global';
import type { IUserState } from '../../../interface/state/IAuthState';
import type { IIRevResultState } from '../../../interface/state/IRev';
import { getPartyColor } from '../upload-result/party-colors';
import { computeKpis, type IDerivedKpis, levelLabel, sortedParties } from '../view-submited-result/utils';
import { buildVerification, type IReceiptVerification } from './verification';

export interface IReceiptField {
	label: string;
	value: string;
	mono?: boolean;
}

export interface IReceiptFigure {
	label: string;
	value: number | undefined;
}

export interface IReceiptParty {
	name: string;
	votes: number;
	color: { bg: string; text: string };
	pct: number; // 0–100, relative to the leader
	isLeader: boolean;
}

export interface IReceiptModel {
	referenceId: string;
	generatedAt: string;
	agentName: string;
	agentId: string;
	partyName: string;
	electionName: string;
	resultType: ResultType;
	levelLabel: string;
	identity: IReceiptField[];
	presidingOfficer: string;
	figures: IReceiptFigure[];
	validVotes: number | undefined;
	kpis: IDerivedKpis;
	parties: IReceiptParty[];
	leader: IReceiptParty | null;
	documentUrls: string[];
	submittedAt: string;
	approvedAt: string | null;
	verification: IReceiptVerification;
	/** True when the read-store result is populated. When false (e.g. the
	 * command→query projection hasn't caught up), the certificate still certifies
	 * the submission from the assignment, but figures/party sections are deferred. */
	figuresAvailable: boolean;
}

const EMPTY = '—';
const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

const isRealRef = (ref?: string | null): boolean => !!ref && ref.trim() !== '' && ref !== ZERO_GUID;

/** Date + time, INEC-style ("Tue. Jun 03. 2026, 14:22"). */
export const fmtDateTime = (value?: string | null): string => {
	if (!value || value.startsWith('0001')) return EMPTY;
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return EMPTY;
	const date = d
		.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' })
		.replace(',', '.');
	const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
	return `${date}, ${time}`;
};

const text = (value?: string | null): string => {
	const v = (value || '').trim();
	return v ? v : EMPTY;
};

interface IAssignmentFallback {
	code?: string;
	name?: string;
}

/** Builds the division hierarchy rows for the agent's collation level. Names come
 * from the read-store result; when that hasn't projected yet, the agent's own
 * entity falls back to the assignment name so the certificate is never blank. */
const buildIdentityHierarchy = (
	result: IIRevResultState,
	resultType: ResultType,
	assignment?: IAssignmentFallback
): IReceiptField[] => {
	const showLga = resultType !== 'EC8D';
	const showWard = resultType === 'EC8A' || resultType === 'EC8B';
	const showPu = resultType === 'EC8A';
	const ownName = (resultName?: string) => (text(resultName) !== EMPTY ? text(resultName) : text(assignment?.name));

	const rows: (IReceiptField | null)[] = [
		{ label: 'State', value: resultType === 'EC8D' ? ownName(result.state?.name) : text(result.state?.name) },
		showLga
			? {
					label: 'LGA',
					value: resultType === 'EC8C' ? ownName(result.localGovernment?.name) : text(result.localGovernment?.name),
				}
			: null,
		showWard ? { label: 'RA / Ward', value: resultType === 'EC8B' ? ownName(result.ward?.name) : text(result.ward?.name) } : null,
		showPu ? { label: 'Polling Unit', value: ownName(result.pollingUnit?.name) } : null,
	];
	return rows.filter(Boolean) as IReceiptField[];
};

/** The geographic code stamped (mono) for the agent's own entity, falling back to
 * the assignment code when the read-store result hasn't projected yet. */
export const entityCode = (result: IIRevResultState, resultType: ResultType, assignment?: IAssignmentFallback): string => {
	const fromResult = (() => {
		switch (resultType) {
			case 'EC8A':
				return result.pollingUnit?.code;
			case 'EC8B':
				return result.ward?.code;
			case 'EC8C':
				return result.localGovernment?.code;
			case 'EC8D':
				return result.state?.code;
			default:
				return '';
		}
	})();
	return text(fromResult) !== EMPTY ? text(fromResult) : text(assignment?.code);
};

/**
 * Just the profile fields the certificate reads. Deliberately structural rather than
 * `IProfileData`: `partyName` is not on `User/GetUserByEmail`'s payload and never has
 * been, so the certificate has always rendered an em-dash for the party. Keeping the
 * field here lets the agent-profile endpoint supply it later without touching this
 * module, and keeps the model unit-testable from a plain literal.
 */
export interface IReceiptProfile {
	firstName?: string | null;
	lastName?: string | null;
	partyName?: string | null;
}

export const buildReceiptModel = (params: {
	result: IIRevResultState;
	official?: { election?: string };
	profile?: IReceiptProfile | null;
	user?: IUserState;
	resultType: ResultType;
	referenceId: string;
	assignment?: IAssignmentFallback;
	now: Date;
}): IReceiptModel => {
	const { result, official, profile, user, resultType, referenceId, assignment, now } = params;

	// The read-store result is "available" only when it actually projected. An empty
	// projection comes back as a 200 with all-null fields + the zero GUID.
	const figuresAvailable = !!(
		isRealRef(result.referenceId) ||
		result.id ||
		result.election?.name ||
		result.results?.length ||
		result.totalValidVotes ||
		result.votersOnRegister
	);

	const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim() || text(user?.FullName);
	const partyName = profile?.partyName?.trim() || EMPTY;

	const kpis = computeKpis(result);
	const ranked = sortedParties(result);
	const max = ranked[0]?.votes || 1;
	const parties: IReceiptParty[] = ranked.map((p, i) => {
		const name = p.partyName || `Party ${p.partyId}`;
		return {
			name,
			votes: p.votes ?? 0,
			color: getPartyColor(name),
			pct: Math.max(((p.votes ?? 0) / max) * 100, 2),
			isLeader: i === 0,
		};
	});

	const figures: IReceiptFigure[] = [
		{ label: 'Registered voters', value: result.votersOnRegister },
		{ label: 'Accredited voters', value: result.accreditedVoters },
		{ label: 'Ballots issued', value: result.ballotPapersIssuedToPoolingUnit },
		{ label: 'Unused ballots', value: result.unusedBallotPapers },
		{ label: 'Rejected ballots', value: result.rejectedBallot },
		{ label: 'Total used ballots', value: result.totalUsedBallotPapers },
	];

	// Canonical, ordered figure string → the integrity fingerprint changes if any
	// recorded number is altered.
	const canonical = [
		result.votersOnRegister,
		result.accreditedVoters,
		result.ballotPapersIssuedToPoolingUnit,
		result.unusedBallotPapers,
		result.rejectedBallot,
		result.totalUsedBallotPapers,
		result.totalValidVotes,
		...ranked.map((p) => `${p.partyId}:${p.votes}`),
	].join('|');

	// Prefer the assignment's real reference over the empty body's zero GUID.
	const ref = isRealRef(result.referenceId) ? result.referenceId : referenceId;

	const identity: IReceiptField[] = [
		{ label: 'Agent', value: fullName },
		{ label: 'Agent ID', value: text(user?.UserId), mono: true },
		{ label: 'Party', value: partyName },
		{ label: 'Result level', value: `${levelLabel(resultType)} (${resultType})` },
		...buildIdentityHierarchy(result, resultType, assignment),
		{ label: `${levelLabel(resultType)} code`, value: entityCode(result, resultType, assignment), mono: true },
	];

	return {
		referenceId: ref,
		figuresAvailable,
		generatedAt: fmtDateTime(now.toISOString()),
		agentName: fullName,
		agentId: text(user?.UserId),
		partyName,
		electionName: result.election?.name?.trim() || text(official?.election),
		resultType,
		levelLabel: levelLabel(resultType),
		identity,
		presidingOfficer: text(result.presidingOfficer?.name),
		figures,
		validVotes: result.totalValidVotes,
		kpis,
		parties,
		leader: parties[0] ?? null,
		documentUrls: (result.documentUrls ?? []).filter(Boolean),
		submittedAt: fmtDateTime(result.createdAt),
		approvedAt: result.approvedBy?.name ? fmtDateTime(result.approvedAt) : null,
		verification: buildVerification(ref, canonical),
	};
};
